/**
 * diagnosisText.js — R14 · PROD-260 (Dr. Rami 2026-10-03), patient P1 step 7.
 * What the patient reads of a diagnosis: its text, never the raw ICD-10 code.
 *
 * ★★ THE RULING: hide raw ICD-10 codes on patient screens, keep the diagnosis text. orella-api
 *   strips the codes from patient-facing responses in D5c; until then (and for anything stored
 *   before) the screens do not render them: AuthorizationsScreen's "ICD-10:" line,
 *   MyMedicalNote's ICD-10 column, its Assessment "(code)" and (review r1 S1, Alex 16:35) the
 *   bracketed codes inside every rendered line of the signed consult note, CaseTimeline's
 *   confirmed-diagnosis chip, Case Home's Diagnose blurb. scripts/check-no-icd-codes.mjs holds
 *   every patient screen to it.
 * ★ ONLY A CODE IN AN UNMISTAKABLE POSITION IS CUT from a text: a whole text that is a bare code,
 *   a code in brackets ("Acute sinusitis (J01.90)", "(ICD-10: J01.90)"), or a code leading the text
 *   before a dash or colon ("J01.90 - Acute sinusitis"). A code-shaped word inside a sentence
 *   stays: "Vitamin B12 deficiency" is a diagnosis, not a code.
 * ★ A CPT procedure code is not an ICD-10 code and is not this ruling.
 * ★★ THE CONSULT-NOTE STRIP HAS ITS OWN, CASE-SENSITIVE PATTERN (review F1, 2026-10-05). It once
 *   reused BRACKETED, whose 'i' flag and letter-digit-alnum shape cut "(q6h)", "(A1C)" and
 *   "(B12)" out of the patient's consult note. BRACKETED (cleanDiagnosisText, billing-code
 *   descriptions) is unchanged; NOTE_BRACKETED is what stripBracketedIcd uses.
 *
 * Pure (no imports).
 */
const CODE = '[A-TV-Z][0-9][0-9A-Z](?:\\.[0-9A-Z]{1,4})?'
const BARE = new RegExp(`^\\s*${CODE}\\s*$`, 'i')
const BRACKETED = new RegExp(`\\s*\\((?:ICD-?10(?:-CM)?:?\\s*)?${CODE}\\)`, 'gi')
const LEADING = new RegExp(`^\\s*(?:ICD-?10(?:-CM)?:?\\s*)?${CODE}\\s*[-–—:]\\s*`, 'i')

// The consult-note strip (review F1). UPPERCASE ONLY, no 'i' flag: a real ICD-10 code is an
// uppercase letter and two digits, or one of the real letter-third categories (C4A, C7A, C7B, D3A,
// M1A, O9A, Z3A), then optionally a dot and 1-4 uppercase alphanumerics. Inside free prose a
// bracket is cut only where the code is unmistakable:
//   - after an "ICD-10:" prefix ("(ICD-10: J30.9)", "(icd-10: I10)"), or
//   - with its decimal part ("(J32.9)", "(J01.90)", "(Z3A.12)"), or
//   - a bare 3-character code that ENDS a clause: end of line, or , ; : . ) ] or a dash after it
//     ("Essential hypertension (I10)", "(I10), ..."). "vitamin (B12) level" keeps "(B12)": a word
//     follows it, so it is a gloss, not a code.
const NOTE_CODE = '(?:[A-Z][0-9]{2}|C4A|C7A|C7B|D3A|M1A|O9A|Z3A)'
const NOTE_DECIMAL = '\\.[0-9A-Z]{1,4}'
const NOTE_PREFIX = '[Ii][Cc][Dd]-?10(?:-[Cc][Mm])?:?\\s*'
const NOTE_BRACKETED = new RegExp(
  `\\s*\\((?:${NOTE_PREFIX}${NOTE_CODE}(?:${NOTE_DECIMAL})?|${NOTE_CODE}${NOTE_DECIMAL}|${NOTE_CODE}(?=\\)\\s*(?:$|[,;:.)\\]\\-–—])))\\)`,
  'g',
)

export const isBareIcdCode = (s) => BARE.test(String(s ?? ''))

/** A diagnosis text with any raw ICD-10 code in an unmistakable position cut out. */
export function cleanDiagnosisText(s) {
  const t = typeof s === 'string' ? s.trim() : ''
  if (!t || isBareIcdCode(t)) return ''
  return t.replace(BRACKETED, '').replace(LEADING, '').trim()
}

/**
 * One rendered line of the signed consult note with every bracketed ICD-10 code cut ("Primary
 * Diagnosis: Chronic sinusitis (J32.9)" -> "Primary Diagnosis: Chronic sinusitis"). Only the
 * bracketed form, and only a real, uppercase ICD-10 shape (NOTE_BRACKETED): "(q6h)", "(A1C)" and
 * "vitamin (B12) level" stay. The rest of the line, its indentation included, is untouched; the
 * stored signed note is never changed.
 */
export const stripBracketedIcd = (line) => (typeof line === 'string' ? line.replace(NOTE_BRACKETED, '') : line)

/** The diagnosis texts of a billing-code list ({ code, description }), codes never included. */
export function diagnosisTextsOf(codes) {
  return (Array.isArray(codes) ? codes : [])
    .map((c) => cleanDiagnosisText(c && typeof c === 'object' ? c.description : ''))
    .filter(Boolean)
}

/**
 * PROD-593 (P1 review N2, 2026-10-05): which diagnosis card CaseTimeline's first encounter shows.
 *   codes          the finalized note's billing codes ({ code, description })
 *   isFinalized    the case is finalized (completed, in follow-up / observation, or a visit summary)
 *   possibleCount  how many pre-visit AI "possible conditions" the case holds
 * -> 'diagnosis'  finalized, and at least one billing code has a diagnosis text (diagnosisTextsOf)
 *    null         finalized with billing codes, none of which has a text: the physician confirmed
 *                 codes, so the pre-visit AI pills are not shown in their place, and no raw code is
 *                 shown either (R14). Measured at 589f548a: such a case fell through to the AI
 *                 "Possible conditions" pills. "View visit summary" still leads to the codes' note.
 *    'possible'   otherwise, when there are possible conditions (not finalized, or finalized with
 *                 no billing codes at all: the older cases, as before)
 *    null         otherwise
 * An entry counts as a billing code when it is an object with a non-empty code or description.
 */
export function diagnosisCardOf(codes, isFinalized, possibleCount) {
  const list = (Array.isArray(codes) ? codes : []).filter((c) => c && typeof c === 'object'
    && (String(c.code ?? '').trim() !== '' || String(c.description ?? '').trim() !== ''))
  if (isFinalized && diagnosisTextsOf(list).length > 0) return 'diagnosis'
  if (isFinalized && list.length > 0) return null
  return possibleCount > 0 ? 'possible' : null
}
